// src/pages/PurchaseOrdersPage.tsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { purchaseOrderService } from '../services/purchaseOrderService';
import axiosInstance from '../utils/axiosConfig';


const PurchaseOrdersPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  
  // State
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: queryParams.get('status') || '',
    performanceId: queryParams.get('performanceId') || '',
    startDate: queryParams.get('startDate') || '',
    endDate: queryParams.get('endDate') || ''
  });

// Add these functions to your React component

// 1. Check current PO status
const checkPOStatus = async () => {
  try {
    const response = await axiosInstance.get('/api/performances/po-status');
    console.log('PO Status:', response.data);
    
    // Create a nice alert with the status
    const status = response.data;
    alert(`
PO Status Report:
• Current Year: ${status.currentYear}
• Sequence Last Number: ${status.sequenceLastNumber}
• Next PO: ${status.nextPO}
• Total POs This Year: ${status.totalPOsThisYear}
• Malformed POs: ${status.malformedCount}

${status.malformedCount > 0 ? 'Run sync to fix malformed POs!' : 'All POs are properly formatted!'}
    `);
  } catch (error) {
    console.error('Error checking PO status:', error);
    alert('Error checking PO status');
  }
};

// 2. Sync PO sequence and fix malformed numbers
const syncPOSequence = async () => {
  try {
    const confirmed = window.confirm(
      'This will sync the PO sequence with your database and fix malformed PO numbers. Continue?'
    );
    
    if (!confirmed) return;
    
    const response = await axiosInstance.post('/api/performances/sync-po-sequence');
    console.log('Sync result:', response.data);
    
    // Show detailed results
    const result = response.data;
    alert(`
✅ PO Sync Complete!

• Current Sequence: ${result.currentSequence}
• Next PO: ${result.nextPO}
• Fixed ${result.fixes.length} malformed PO numbers

${result.fixes.length > 0 ? 
  'Fixed POs:\n' + result.fixes.map((f: { oldPO: string; newPO: string }) => `${f.oldPO} → ${f.newPO}`).join('\n') 
  : 'No malformed POs found!'}
    `);
  } catch (error) {
    console.error('Error syncing PO sequence:', error);
    alert('Error syncing PO sequence');
  }
};

// 3. Original fix function (now deprecated, but keeping for reference)
const fixPONumbers = async () => {
  try {
    const response = await axiosInstance.post('/api/performances/fix-po-numbers');
    console.log('Fixed PO numbers:', response.data);
    alert(`Fixed ${response.data.fixes.length} PO numbers`);
  } catch (error) {
    console.error('Error fixing PO numbers:', error);
    alert('Error fixing PO numbers - try sync instead');
  }
};
  
  // Load POs
  useEffect(() => {
    const fetchPOs = async () => {
      try {
        setLoading(true);
        const pos = await purchaseOrderService.getPurchaseOrders(filters);
        setPurchaseOrders(pos);
        setError(null);
      } catch (error) {
        console.error('Error fetching purchase orders:', error);
        setError('Failed to load purchase orders');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPOs();
  }, [filters]);
  
  // Handle filter changes
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    
    // Update URL params
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set(name, value);
    } else {
      params.delete(name);
    }
    navigate(`${location.pathname}?${params.toString()}`);
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Status chip color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created': return 'default';
      case 'approved': return 'primary';
      case 'partially_invoiced': return 'info';
      case 'fully_invoiced': return 'secondary';
      case 'partially_paid': return 'warning';
      case 'fully_paid': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>        
          <Typography variant="h4">Purchase Orders</Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={() => {
              // Refresh POs
              const fetchPOs = async () => {
                try {
                  setLoading(true);
                  const pos = await purchaseOrderService.getPurchaseOrders(filters);
                  setPurchaseOrders(pos);
                } catch (error) {
                  console.error('Error refreshing POs:', error);
                } finally {
                  setLoading(false);
                }
              };
              
              fetchPOs();
            }}
          >
            Refresh
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="partially_invoiced">Partially Invoiced</MenuItem>
                  <MenuItem value="fully_invoiced">Fully Invoiced</MenuItem>
                  <MenuItem value="partially_paid">Partially Paid</MenuItem>
                  <MenuItem value="fully_paid">Fully Paid</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilters({
                      status: '',
                      performanceId: '',
                      startDate: '',
                      endDate: ''
                    });
                    
                    // Clear URL params
                    navigate(location.pathname);
                  }}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Box>
              
            </Grid>
          </Grid>
          <div style={{ padding: '20px', border: '1px solid #ccc', margin: '20px' }}>
  <h3>PO Number Management</h3>
  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
    <button 
      onClick={checkPOStatus}
      style={{ 
        padding: '10px 15px', 
        backgroundColor: '#2196F3', 
        color: 'white', 
        border: 'none', 
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      📊 Check PO Status
    </button>
    
    <button 
      onClick={syncPOSequence}
      style={{ 
        padding: '10px 15px', 
        backgroundColor: '#4CAF50', 
        color: 'white', 
        border: 'none', 
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      🔄 Sync PO Sequence
    </button>
    
    <button 
      onClick={fixPONumbers}
      style={{ 
        padding: '10px 15px', 
        backgroundColor: '#FF9800', 
        color: 'white', 
        border: 'none', 
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      🔧 Fix PO Numbers (Old)
    </button>
  </div>
  <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
    💡 Use "Check PO Status" first, then "Sync PO Sequence" to fix issues
  </p>
</div>
        </Paper>
        
        {/* Purchase Orders Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : purchaseOrders.length === 0 ? (
          <Alert severity="info">
            No purchase orders found. 
            {filters.performanceId || filters.status || filters.startDate || filters.endDate ? 
              " Try adjusting your filters." : ""}
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Artist/Band</TableCell>
                  <TableCell>Event Date</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Invoiced</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((po) => (
                  <TableRow key={po._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>{po.poNumber}</TableCell>
                    <TableCell>{po.bandName}</TableCell>
                    <TableCell>{formatDate(po.eventDate)}</TableCell>
                    <TableCell>{formatCurrency(po.totalAmount)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={po.status.replace('_', ' ')}
                        color={getStatusColor(po.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatCurrency(po.invoicedAmount)} / {formatCurrency(po.totalAmount)}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(po.paidAmount)} / {formatCurrency(po.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/admin/purchase-orders/${po._id}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Container>
  );
};

export default PurchaseOrdersPage;